import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge, SeverityBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Send, Pencil, FileCode, Loader2, ShieldAlert,
  Rocket, Check, AlertTriangle, Upload, Download, Eye, Trash2, FileText,
  ShieldCheck, ShieldX, Landmark,
} from "lucide-react";

const DOC_TYPE_OPTIONS = [
  ["commercial_invoice", "Commercial Invoice"], ["packing_list", "Packing List"],
  ["bill_of_lading", "Bill of Lading"], ["cmr", "CMR"], ["certificate", "Certificate"],
  ["customs_declaration", "Customs Declaration"], ["other", "Other"],
];

function Panel({ title, icon: Icon, children, action }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-900/40">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
        {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500" />}
        <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium">{title}</span>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function Field({ label, value, mono = true }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-zinc-800/40 last:border-0">
      <span className="text-[11px] text-zinc-500 uppercase tracking-wide">{label}</span>
      <span className={`text-xs text-right ${mono ? "font-mono" : ""} text-zinc-200`}>{value || "—"}</span>
    </div>
  );
}

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editHs, setEditHs] = useState("");
  const [showZzi, setShowZzi] = useState(false);
  const [lineHs, setLineHs] = useState({});
  const [saveFuture, setSaveFuture] = useState(true);

  const { data: t, isLoading } = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: async () => (await api.get(`/tickets/${ticketId}`)).data,
    refetchInterval: (q) => {
      const d = q.state.data;
      if (["acknowledged", "classifying", "drafting_report"].includes(d?.status)) return 4000;
      if (d?.status === "report_ready" && !d?.report_html) return 4000;
      return false;
    },
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["ticket", ticketId] }); qc.invalidateQueries({ queryKey: ["tickets"] }); };

  const reclassify = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${ticketId}/reclassify`)).data,
    onSuccess: () => { toast.success("Re-classification complete"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const saveHs = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${ticketId}/edit-hs`, { hs_code: editHs })).data,
    onSuccess: () => { toast.success("HS code updated"); setEditHs(""); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const sendReport = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${ticketId}/send-report`)).data,
    onSuccess: (d) => { d.sent ? toast.success("Report sent in-thread") : toast.error(`Send failed: ${d.error || "email error"}`); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const reparse = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${ticketId}/reparse`)).data,
    onSuccess: () => { toast.success("Parsing documents… classification will update shortly"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const setIco = useMutation({
    mutationFn: async (val) => (await api.post(`/tickets/${ticketId}/flags`, { ico_cert: val })).data,
    onSuccess: () => { toast.success("ICO cert flag updated"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const setProc = useMutation({
    mutationFn: async (p) => (await api.post(`/tickets/${ticketId}/flags`, { procedure: p })).data,
    onSuccess: () => { toast.success("Procedure updated"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const setPf = useMutation({
    mutationFn: async (pf) => (await api.post(`/tickets/${ticketId}/flags`, { pf_number: pf })).data,
    onSuccess: () => { toast.success("PF number saved (goes to UCR / opravilna vrstica)"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const setT1 = useMutation({
    mutationFn: async (patch) => (await api.post(`/tickets/${ticketId}/flags`, patch)).data,
    onSuccess: () => { toast.success("Transit field saved"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const revalidate = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${ticketId}/validate-parties`)).data,
    onSuccess: () => { toast.success("VAT/EORI re-validated"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const setLineHsMut = useMutation({
    mutationFn: async ({ lineId, code }) => (await api.post(`/tickets/${ticketId}/lines/${lineId}/hs`, { hs_code: code, save_rule: saveFuture, scope: "importer" })).data,
    onSuccess: () => { toast.success("Line HS updated" + (saveFuture ? " & saved for this importer's future imports" : "")); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (isLoading) return <div className="p-10 text-center text-zinc-500"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (!t) return <div className="p-10 text-zinc-500">Ticket not found.</div>;

  const cls = t.classification || {};
  const duty = t.duty || {};
  const compliance = t.compliance || [];

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-3 sticky top-0 bg-background z-10">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="p-1.5 border border-zinc-800 rounded-sm hover:bg-zinc-900 text-zinc-400"><ArrowLeft className="h-4 w-4" /></button>
        <h1 className="font-heading text-base font-semibold tracking-tight font-mono text-blue-400">{t.ticket_id}</h1>
        <StatusBadge status={t.status} />
        <span className="text-xs font-mono text-zinc-500">{t.procedure_label}</span>
        <select data-testid="procedure-select" value={t.procedure} onChange={(e) => setProc.mutate(e.target.value)}
          className="text-xs bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1.5 text-zinc-200 focus:border-blue-500 outline-none">
          <option value="4000">40 – free circ.</option>
          <option value="4200">42 – onward supply</option>
          <option value="7100">71 – warehousing</option>
          <option value="T1">T1 – transit</option>
          <option value="EXPORT">Export</option>
        </select>
        <input data-testid="pf-number-input" defaultValue={t.pf_number || ""} placeholder="PF / order no."
          onBlur={(e) => { if ((e.target.value || "") !== (t.pf_number || "")) setPf.mutate(e.target.value); }}
          className="w-32 text-xs bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1.5 font-mono text-zinc-200 focus:border-blue-500 outline-none" title="PF/order number — written to UCR (opravilna vrstica) on the MRN" />
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="reparse-btn" disabled={reparse.isPending} onClick={() => reparse.mutate()}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 disabled:opacity-50">
            {reparse.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCode className="h-3.5 w-3.5" />} Re-parse docs
          </button>
          <button data-testid="ico-cert-btn" onClick={() => setIco.mutate(!t.ico_cert)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-sm ${t.ico_cert ? "border-emerald-600 text-emerald-400 bg-emerald-900/20" : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"}`}>
            <Check className="h-3.5 w-3.5" /> ICO cert {t.ico_cert ? "on file" : "off"}
          </button>
          <button data-testid="reclassify-btn" disabled={reclassify.isPending} onClick={() => reclassify.mutate()}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 disabled:opacity-50">
            {reclassify.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Force re-classify
          </button>
          <button data-testid="send-report-btn" disabled={sendReport.isPending || !t.report_html} onClick={() => sendReport.mutate()}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-emerald-700 text-emerald-400 rounded-sm hover:bg-emerald-900/30 disabled:opacity-50">
            {sendReport.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Approve & send report
          </button>
          <button data-testid="open-zzi-btn" onClick={() => setShowZzi(true)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-sm">
            <Rocket className="h-3.5 w-3.5" /> Approve & submit to ZZI
          </button>
        </div>
      </header>

      <div className="p-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="space-y-4">
          <Panel title="Request" icon={FileCode}>
            <Field label="Procedure" value={t.procedure} />
            <Field label="From" value={t.from_email} />
            <Field label="Origin" value={t.origin_country} />
            <Field label="Destination" value={t.destination_country} />
            <Field label="Quantity" value={t.quantity} />
            <Field label="Gross mass" value={t.gross_mass_kg} />
            <Field label="Customs value" value={t.customs_value} />
            <Field label="Importer" value={t.importer} mono={false} />
            <Field label="Exporter" value={t.exporter} mono={false} />
            <Field label="Incoterm" value={t.incoterm} />
          </Panel>
          {t.procedure === "T1" && <T1TransitPanel t={t} onSave={(patch) => setT1.mutate(patch)} />}
          <Panel title="Original email">
            <pre className="text-[11px] font-mono text-zinc-400 whitespace-pre-wrap max-h-56 overflow-auto">{t.request_text}</pre>
          </Panel>
          {(t.attachment_names || []).length > 0 && !!t.documents_excerpt && (
            <Panel title="Parsed text (source email attachments)" icon={FileCode}>
              <pre className="text-[10px] font-mono text-zinc-600 whitespace-pre-wrap max-h-40 overflow-auto">{t.documents_excerpt}</pre>
            </Panel>
          )}
          <DocumentsPanel ticketId={ticketId} onChange={invalidate} />
          <PartyValidationPanel pv={t.party_validation} onValidate={() => revalidate.mutate()} loading={revalidate.isPending} />
        </div>

        <div className="space-y-4">
          {(t.line_items || []).length >= 1 && (
            <Panel title={`Line items (${t.line_items.length})`} icon={FileCode}>
              <label className="flex items-center gap-1.5 text-[10px] text-zinc-400 mb-2 cursor-pointer">
                <input type="checkbox" data-testid="save-future-toggle" checked={saveFuture} onChange={(e) => setSaveFuture(e.target.checked)} />
                Remember HS edits for this importer's future imports
              </label>
              <div className="space-y-1.5 max-h-96 overflow-auto">
                {t.line_items.map((li) => (
                  <div key={li.line_id} data-testid={`line-item-${li.line_id}`} className="text-xs border border-zinc-800 rounded-sm p-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-blue-400">{li.hs_code || "—"}</span>
                      {li.duty?.mfn_duty_pct != null && <span className="text-zinc-500">{li.duty.mfn_duty_pct}%</span>}
                      {li.rule_applied && <span className="text-[9px] font-mono text-emerald-400 border border-emerald-500/40 rounded-sm px-1">RULE</span>}
                      <span className="ml-auto text-zinc-500">{li.customs_value || ""}</span>
                    </div>
                    <p className="text-zinc-300 mt-1">{li.description}</p>
                    <div className="flex items-center gap-1 mt-1.5">
                      <input data-testid={`line-hs-input-${li.line_id}`} defaultValue={li.hs_code}
                        onChange={(e) => setLineHs({ ...lineHs, [li.line_id]: e.target.value })}
                        className="flex-1 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1 text-[11px] font-mono focus:border-blue-500 outline-none" />
                      <button data-testid={`line-hs-save-${li.line_id}`}
                        onClick={() => setLineHsMut.mutate({ lineId: li.line_id, code: lineHs[li.line_id] ?? li.hs_code })}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 border border-emerald-700 text-emerald-400 rounded-sm hover:bg-emerald-900/30">
                        <Check className="h-3 w-3" /> Save
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          <Panel title="Classification reasoning" icon={Rocket}>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-lg text-white">{cls.chosen_hs_code || "—"}</span>
              {cls.confidence != null && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-blue-500/40 text-blue-400">{cls.confidence}%</span>}
              {cls.gri_rule && <span className="text-[10px] font-mono text-zinc-500">{cls.gri_rule}</span>}
              {cls.manually_edited && <span className="text-[10px] font-mono text-amber-400">EDITED</span>}
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed mb-2">{cls.reasoning || "Pipeline running…"}</p>
            {cls.taric_description && <p className="text-[11px] font-mono text-zinc-500">TARIC: {cls.taric_description}</p>}
            {cls.alternative_codes?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {cls.alternative_codes.map((c) => <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 border border-zinc-700 rounded-sm text-zinc-400">{c}</span>)}
              </div>
            )}
          </Panel>

          <Panel title="Candidate HS codes">
            <div className="space-y-1 max-h-56 overflow-auto">
              {(t.candidate_hs || []).map((c) => (
                <div key={c.hs_code} className={`flex items-start gap-2 text-xs p-1.5 rounded-sm ${c.hs_code === cls.chosen_hs_code ? "bg-blue-500/10 border border-blue-500/30" : "hover:bg-zinc-900"}`}>
                  <span className="font-mono text-zinc-200">{c.hs_code}</span>
                  <span className="text-zinc-500 flex-1">{c.description}</span>
                  <span className="font-mono text-zinc-400">{c.mfn_duty}%</span>
                </div>
              ))}
              {(t.candidate_hs || []).length === 0 && <p className="text-xs text-zinc-600">No candidates.</p>}
            </div>
          </Panel>

          <Panel title="Edit HS code" icon={Pencil}>
            <div className="flex gap-2">
              <input data-testid="edit-hs-input" value={editHs} onChange={(e) => setEditHs(e.target.value)} placeholder={cls.chosen_hs_code || "HS code"}
                className="flex-1 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs font-mono focus:border-blue-500 outline-none" />
              <button data-testid="save-hs-btn" disabled={!editHs || saveHs.isPending} onClick={() => saveHs.mutate()}
                className="flex items-center gap-1 text-xs px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                {saveHs.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
              </button>
            </div>
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel title="Duty breakdown">
            <Field label="MFN duty" value={duty.mfn_duty_pct != null ? `${duty.mfn_duty_pct}%` : "—"} />
            {duty.estimated_duty != null && <Field label="Est. duty" value={`EUR ${duty.estimated_duty}`} />}
            <Field label="Preferential" value={duty.preferential_available ? "Available" : "None"} />
            <div className="mt-2 space-y-1">
              {(duty.lines || []).map((l, i) => (
                <div key={i} className="flex justify-between text-[11px] py-1 border-b border-zinc-800/40">
                  <span className="text-zinc-500">{l.type}</span>
                  <span className="font-mono text-zinc-300 text-right">{l.rate}</span>
                </div>
              ))}
            </div>
            {duty.note && <p className="text-[10px] text-zinc-600 mt-2 italic">{duty.note}</p>}
          </Panel>

          <Panel title="Compliance flags" icon={ShieldAlert}>
            {compliance.length === 0 && <p className="text-xs text-zinc-600">No compliance flags detected.</p>}
            <div className="space-y-1.5">
              {compliance.map((c, i) => (
                <div key={i} className="flex items-start gap-2">
                  <SeverityBadge severity={c.severity}>{c.regime}</SeverityBadge>
                  <span className="text-[11px] text-zinc-400 flex-1">{c.label}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Report preview">
            {t.report_html
              ? <div className="text-xs text-zinc-300 max-h-72 overflow-auto prose-invert [&_h3]:font-heading [&_h3]:text-sm [&_h3]:mb-2 [&_p]:mb-1.5 [&_b]:text-white" dangerouslySetInnerHTML={{ __html: t.report_html }} />
              : <p className="text-xs text-zinc-600">Report will appear when the pipeline completes.</p>}
          </Panel>
        </div>
      </div>

      {showZzi && <ZziModal ticket={t} onClose={() => setShowZzi(false)} onDone={invalidate} />}
    </div>
  );
}

function IdBadge({ res, kind }) {
  if (!res) return <span className="text-[10px] text-zinc-600">{kind}: not found</span>;
  const valid = res.valid;
  const meta = valid === true
    ? { Icon: ShieldCheck, cls: "text-emerald-400", txt: "VALID" }
    : valid === false
      ? { Icon: ShieldX, cls: "text-red-400", txt: "INVALID" }
      : { Icon: AlertTriangle, cls: "text-amber-400", txt: "UNVERIFIED" };
  return (
    <div className="flex items-start gap-1.5" data-testid={`val-${kind.toLowerCase()}`}>
      <meta.Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${meta.cls}`} />
      <div className="min-w-0">
        <span className={`text-[11px] font-mono ${meta.cls}`}>{kind} {res.number} · {meta.txt}</span>
        {res.name && <p className="text-[10px] text-zinc-500 truncate">{res.name}</p>}
        <span className="text-[9px] text-zinc-600">{res.source}</span>
      </div>
    </div>
  );
}

function PartyValidationPanel({ pv, onValidate, loading }) {
  const roles = pv ? Object.keys(pv) : [];
  return (
    <Panel title="Party validation (VIES / EOS)" icon={Landmark}>
      <div data-testid="party-validation-panel" className="space-y-3">
        <button data-testid="revalidate-btn" onClick={onValidate} disabled={loading}
          className="w-full flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 disabled:opacity-50">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Validate VAT / EORI now
        </button>
        {roles.length === 0 && <p className="text-[11px] text-zinc-600 text-center">Not validated yet.</p>}
        {roles.map((role) => {
          const p = pv[role];
          const fr = p.fiscal_rep || {};
          return (
            <div key={role} data-testid={`party-${role}`} className="border border-zinc-800 rounded-sm p-2.5 bg-zinc-950/40 space-y-1.5">
              <p className="text-[10px] uppercase tracking-wide text-zinc-500 capitalize">{role}</p>
              <IdBadge res={p.vat} kind="VAT" />
              <IdBadge res={p.eori} kind="EORI" />
              {fr.is_rep ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-emerald-600/50 text-emerald-400 bg-emerald-900/20">
                  <Check className="h-3 w-3" /> Fiscal rep (by {fr.matched_by})
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-500">
                  Not a fiscal-rep customer
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function docIcon(ct) {
  if ((ct || "").includes("pdf")) return FileText;
  if ((ct || "").includes("image")) return Eye;
  return FileCode;
}

function DocumentsPanel({ ticketId, onChange }) {
  const qc = useQueryClient();
  const [drag, setDrag] = useState(false);
  const [viewer, setViewer] = useState(null); // {url, name, content_type}
  const token = localStorage.getItem("roket_token");

  useEffect(() => {
    if (!viewer) return;
    const onKey = (e) => { if (e.key === "Escape") setViewer(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer]);

  const { data: docs = [] } = useQuery({
    queryKey: ["ticket-docs", ticketId],
    queryFn: async () => (await api.get(`/tickets/${ticketId}/documents`)).data,
    refetchInterval: 6000,
  });

  const upload = useMutation({
    mutationFn: async (files) => {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      return (await api.post(`/tickets/${ticketId}/documents`, fd,
        { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      toast.success(`${d.documents.length} document(s) uploaded & classified${d.reparsing ? " — re-classifying" : ""}`);
      qc.invalidateQueries({ queryKey: ["ticket-docs", ticketId] });
      onChange && onChange();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const setType = useMutation({
    mutationFn: async ({ id, doc_type }) => (await api.patch(`/documents/${id}`, { doc_type })).data,
    onSuccess: () => { toast.success("Type updated"); qc.invalidateQueries({ queryKey: ["ticket-docs", ticketId] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/documents/${id}`)).data,
    onSuccess: () => { toast.success("Document removed"); qc.invalidateQueries({ queryKey: ["ticket-docs", ticketId] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const dlUrl = (id, disp) => `${API}/documents/${id}/download?disposition=${disp}&token=${encodeURIComponent(token)}`;
  const openView = (d) => {
    const url = dlUrl(d.id, "inline");
    if ((d.content_type || "").includes("pdf") || (d.content_type || "").includes("image")) {
      setViewer({ url, name: d.original_filename, content_type: d.content_type });
    } else {
      window.open(url, "_blank");
    }
  };

  return (
    <Panel title={`Documents (${docs.length})`} icon={FileCode}>
      <div data-testid="doc-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) upload.mutate(e.dataTransfer.files); }}
        className={`border border-dashed rounded-sm p-4 text-center transition-colors duration-150 mb-3 ${drag ? "border-blue-500 bg-blue-500/5" : "border-zinc-700"}`}>
        {upload.isPending ? (
          <div className="flex items-center justify-center gap-2 text-xs text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /> Uploading & classifying…</div>
        ) : (
          <>
            <Upload className="h-5 w-5 mx-auto mb-1.5 text-zinc-500" />
            <p className="text-[11px] text-zinc-400">Drag & drop invoices, packing lists, B/L, CMR…</p>
            <label className="inline-block mt-2 text-[11px] text-blue-400 hover:text-blue-300 cursor-pointer" data-testid="doc-browse">
              or browse files
              <input type="file" multiple className="hidden" accept=".pdf,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.webp"
                onChange={(e) => e.target.files?.length && upload.mutate(e.target.files)} />
            </label>
            <p className="text-[9px] text-zinc-600 mt-1">PDF, XLS/XLSX, images · max 25 MB · auto re-classifies the ticket</p>
          </>
        )}
      </div>

      <div className="space-y-2">
        {docs.length === 0 && <p className="text-[11px] text-zinc-600 text-center py-1">No documents yet.</p>}
        {docs.map((d) => {
          const Icon = docIcon(d.content_type);
          return (
            <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-2 border border-zinc-800 rounded-sm px-2 py-1.5 bg-zinc-950/40">
              <Icon className="h-4 w-4 text-zinc-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-zinc-200 truncate" title={d.original_filename}>{d.original_filename}</p>
                <div className="flex items-center gap-1.5">
                  <select data-testid={`doc-type-${d.id}`} value={d.doc_type}
                    onChange={(e) => setType.mutate({ id: d.id, doc_type: e.target.value })}
                    className="bg-zinc-900 border border-zinc-700 rounded-sm text-[9px] text-zinc-300 px-1 py-0.5 outline-none focus:border-blue-500">
                    {DOC_TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {d.doc_type_auto && d.doc_type_confidence != null && (
                    <span className="text-[9px] text-zinc-600">AI {d.doc_type_confidence}%</span>
                  )}
                </div>
              </div>
              <button data-testid={`doc-view-${d.id}`} onClick={() => openView(d)} title="View"
                className="p-1 text-zinc-400 hover:text-blue-400"><Eye className="h-3.5 w-3.5" /></button>
              <a data-testid={`doc-download-${d.id}`} href={dlUrl(d.id, "attachment")} title="Download"
                className="p-1 text-zinc-400 hover:text-blue-400"><Download className="h-3.5 w-3.5" /></a>
              <button data-testid={`doc-delete-${d.id}`} onClick={() => remove.mutate(d.id)} title="Remove"
                className="p-1 text-zinc-400 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
      </div>

      {viewer && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center backdrop-blur-md bg-zinc-950/85 p-4" onClick={() => setViewer(null)}>
          <div className="w-full max-w-4xl h-[85vh] bg-zinc-900 border border-zinc-800 rounded-sm flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
              <FileText className="h-4 w-4 text-zinc-500" />
              <span className="text-xs text-zinc-300 truncate">{viewer.name}</span>
              <a href={viewer.url.replace("disposition=inline", "disposition=attachment")} className="ml-auto text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Download</a>
              <button onClick={() => setViewer(null)} className="text-zinc-400 hover:text-white text-sm px-2">✕</button>
            </div>
            <div className="flex-1 bg-zinc-950 overflow-auto flex items-center justify-center">
              {(viewer.content_type || "").includes("image")
                ? <img src={viewer.url} alt={viewer.name} className="max-w-full max-h-full object-contain" />
                : <iframe title={viewer.name} src={viewer.url} className="w-full h-full" />}
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

function T1TransitPanel({ t, onSave }) {
  const fields = [
    ["t1_office_departure", "Office of departure", "SI006044"],
    ["t1_office_destination", "Office of destination", "e.g. NL000205"],
    ["t1_holder_name", "Holder (principal) name", "EUROPACIFIC d.o.o."],
    ["t1_holder_eori", "Holder EORI", "SIxxxxxxxx"],
    ["t1_guarantee_grn", "Guarantee GRN", "e.g. DE6382665"],
    ["t1_previous_doc", "Previous doc (VCP / sedmica)", "N705-26SI006044P0088151"],
    ["t1_container", "Container no.", "e.g. EGSU8011100"],
    ["t1_transport_reg", "Truck registration", "reg. plate"],
    ["t1_cmr", "CMR number", "CMR ref"],
  ];
  return (
    <Panel title="Transit details (T1 / NCTS IE015)" icon={FileCode}>
      <div className="space-y-2" data-testid="t1-transit-panel">
        {fields.map(([key, label, ph]) => (
          <div key={key} className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</label>
            <input data-testid={`t1-input-${key}`} defaultValue={t[key] || ""} placeholder={ph}
              onBlur={(e) => { if ((e.target.value || "") !== (t[key] || "")) onSave({ [key]: e.target.value }); }}
              className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-[11px] font-mono text-zinc-200 focus:border-blue-500 outline-none" />
          </div>
        ))}
        <p className="text-[10px] text-zinc-600 pt-1">Saved values feed the NCTS IE015 transit declaration. Edits persist on blur.</p>
      </div>
    </Panel>
  );
}

function ZziModal({ ticket, onClose, onDone }) {
  const { data, isLoading } = useQuery({
    queryKey: ["zzi-draft", ticket.ticket_id],
    queryFn: async () => (await api.get(`/tickets/${ticket.ticket_id}/zzi/draft`)).data,
  });
  const submit = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${ticket.ticket_id}/zzi/submit`, data.fields)).data,
    onSuccess: (r) => {
      toast.success(r.simulated ? `Simulated submission — MRN ${r.mrn}` : `Submitted to ZZI — MRN ${r.mrn}`);
      onDone(); onClose();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-zinc-950/80 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl bg-zinc-900 border border-zinc-800 rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800">
          <FileCode className="h-4 w-4 text-blue-500" />
          <h3 className="font-heading font-semibold text-sm">ZZI eCustoms Declaration — {ticket.procedure === "EXPORT" ? "IE515 (AES)" : ticket.procedure === "T1" ? "NCTS (T1)" : "IE415B (AIS)"}</h3>
          {data?.safe_mode && (
            <span className="ml-auto flex items-center gap-1 text-[10px] font-mono px-2 py-1 border border-amber-500/40 text-amber-400 rounded-sm">
              <AlertTriangle className="h-3 w-3" /> SAFE MODE — no live push
            </span>
          )}
        </div>
        <div className="p-4">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-zinc-500" /> : (
            <>
              <p className="text-[11px] text-zinc-500 mb-2 uppercase tracking-wider">Generated ZZI {ticket.procedure === "T1" ? "NCTS IE015 (transit)" : ticket.procedure === "EXPORT" ? "IE515 (AES)" : "IE415B (AIS)"} JSON declaration</p>
              <pre data-testid="zzi-xml" className="text-[11px] font-mono text-emerald-300 bg-zinc-950 border border-zinc-800 rounded-sm p-3 max-h-80 overflow-auto whitespace-pre-wrap">
{JSON.stringify(data?.declaration || {}, null, 2)}
              </pre>
              <p className="text-[10px] text-zinc-600 mt-2">
                {data?.safe_mode
                  ? "Safe mode is active: submission is validated and queued locally. Set ZZI_SAFE_MODE=false to push to production."
                  : "This will push a real declaration to ZZI production (ws.zzi.si)."}
              </p>
              {data?.requirements && (
                <div className="mt-3 text-[11px]" data-testid="zzi-requirements">
                  <p className="uppercase tracking-wider text-zinc-500 mb-1">Required data — {data.requirements.module}</p>
                  {!data.requirements.live_supported && (
                    <p className="text-amber-400 mb-1 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Live submission for this procedure is not yet integrated — safe-mode simulation only.</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {[...data.requirements.common, ...data.requirements.specific].map((r, i) => (
                      <span key={i} className="px-1.5 py-0.5 border border-zinc-800 rounded-sm text-zinc-400 font-mono">{r}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-zinc-800">
          <button onClick={onClose} className="text-sm px-3 py-2 border border-zinc-700 rounded-sm hover:bg-zinc-800">Cancel</button>
          <button data-testid="zzi-submit-btn" disabled={submit.isPending || isLoading} onClick={() => submit.mutate()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-sm">
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Approve & submit
          </button>
        </div>
      </div>
    </div>
  );
}
